import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchOrders,
  fetchStatusCounts,
  selectAllOrders,
  selectStatusCounts,
} from "../../../slice/order/orderSlice";
import api from "../../../api/axios";
import { toast } from "react-toastify";
import { DATE_PRESETS, dateInRange } from "../../../utils/dateRange";

/* ─── Delivery Mode Config ───────────────────────────────────── */
const DELIVERY_MODES = [
  { label: "Home Delivery", value: "delivery" },
  { label: "On-Store Pickup", value: "pickup" },
];

/* ─── Brand palette ──────────────────────────────────────────── */
const BRAND = {
  forest: "#0E3A2C", pine: "#1A5440", gold: "#C9952A", honey: "#E8C96A",
  cream: "#FDF6E3", sand: "#F1E6C9", ink: "#15281D", stone: "#4C5C46",
  fail: "#EF5350",
};

/* ─── Status filter chips (comma-separated sets → ?status=) ──── */
const STATUS_FILTERS = [
  { key: "all",       label: "All",              statuses: [] },
  { key: "new",       label: "New",              statuses: ["pending", "accepted"] },
  { key: "assigned",  label: "Assigned",         statuses: ["assigned"] },
  { key: "milling",   label: "Milling/Packing",  statuses: ["preparing"] },
  { key: "done",      label: "Done",             statuses: ["processing_done", "ready"] },
  { key: "waiting",   label: "Waiting/Ready",    statuses: ["waiting_delivery", "ready_pickup", "out_pickup"] },
  { key: "outfd",     label: "Out for delivery", statuses: ["out_for_delivery", "out_delivery", "shipped"] },
  { key: "delivered", label: "Delivered",        statuses: ["delivered", "left_at_doorstep"] },
  { key: "failed",    label: "Failed",           statuses: ["delivery_failed", "could_not_deliver"] },
  { key: "completed", label: "Completed",        statuses: ["completed"] },
  { key: "cancelled", label: "Cancelled",        statuses: ["cancelled"] },
];

/* GET /admin/orders/status-counts answers ONE request for every chip, and
   keys {counts} by a SINGLE status — never by a chip's comma-joined set. So
   we ask for every status any chip needs and each chip sums its own.

   TODO(cutover): slice/order/orderSlice.js was NOT cut over — fetchOrders and
   fetchStatusCounts still call GET /api/orders (Node path, Node envelope), so
   this list and its chips 404 on the .NET service. The slice needs
   /admin/orders (payload is {data, pagination}, unchanged) and
   fetchStatusCounts should collapse to one GET /admin/orders/status-counts
   ?status=<csv>&deliveryMode=<mode>, reading res.data.counts. Nothing in
   THIS file can reach the server until that happens. */
const COUNTED_STATUSES = Array.from(
  new Set(STATUS_FILTERS.flatMap((f) => f.statuses))
);

/* ─── Full status → label map (modern + legacy) ──────────────── */
const STATUS_LABEL = {
  pending:           "Ordered",
  accepted:          "Accepted",
  assigned:          "Assigned",
  preparing:         "Milling/Packing",
  processing_done:   "Milling/Packing Done",
  waiting_delivery:  "Waiting Delivery",
  ready_pickup:      "Ready for Pickup",
  out_for_delivery:  "Out for Delivery",
  delivered:         "Delivered",
  delivery_failed:   "Delivery Failed",
  left_at_doorstep:  "Left at Doorstep",
  completed:         "Completed",
  could_not_deliver: "Could Not Deliver",
  cancelled:         "Cancelled",
  /* legacy */
  ready:        "Order Ready",
  out_delivery: "Out for Delivery",
  out_pickup:   "Out for Pickup",
  shipped:      "Shipped",
};

/* The page numbers actually worth drawing: the first, the last, and a few
   either side of where the reader is, with nothing in between rendered at all.
   A shop with five thousand orders has five hundred pages, and drawing a
   button for each of them on every ten-second refresh is most of what that
   refresh costs. */
const pageWindow = (page, totalPages) => {
  const total = Number(totalPages) || 0;
  if (total <= 1) return total === 1 ? [1] : [];
  const near = new Set([1, total, page, page - 1, page + 1, page - 2, page + 2]);
  return [...near].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
};

const SUCCESS_STATUSES = ["delivered", "left_at_doorstep", "completed"];
const FAIL_STATUSES    = ["delivery_failed", "could_not_deliver"];

/* gold = active pipeline · pine = success · red = fail · stone = cancelled */
const statusChipStyle = (status) => {
  if (FAIL_STATUSES.includes(status))
    return { bg: "#FDECEA", color: "#C62828", border: BRAND.fail };
  if (SUCCESS_STATUSES.includes(status))
    return { bg: "#E4F0EA", color: BRAND.pine, border: "#9CC7B4" };
  if (status === "cancelled")
    return { bg: "#EFF1ED", color: BRAND.stone, border: "#C9D1C4" };
  return { bg: BRAND.cream, color: BRAND.gold, border: BRAND.honey };
};

/* ─── Next-status map along the modern pipeline (per mode) ────── */
/* Legacy sources advance as their modern equivalent:
   ready≈processing_done · out_delivery/shipped≈out_for_delivery · out_pickup≈ready_pickup */
const NEXT_STATUS = {
  delivery: {
    pending:           "accepted",
    accepted:          "assigned",
    assigned:          "preparing",
    preparing:         "processing_done",
    processing_done:   "waiting_delivery",
    waiting_delivery:  "out_for_delivery",
    out_for_delivery:  "delivered",
    delivered:         "completed",
    left_at_doorstep:  "completed",
    delivery_failed:   "out_for_delivery",
    could_not_deliver: "out_for_delivery",
    ready:             "waiting_delivery",
    out_delivery:      "delivered",
    shipped:           "delivered",
  },
  pickup: {
    pending:         "accepted",
    accepted:        "assigned",
    assigned:        "preparing",
    preparing:       "processing_done",
    processing_done: "ready_pickup",
    ready_pickup:    "completed",
    delivered:       "completed",
    ready:           "ready_pickup",
    out_pickup:      "completed",
  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

/* Every amount on /admin/orders is an integer number of PAISE. */
const rupees = (paise) =>
  ((Number(paise) || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const PendingActionModal = ({ order, onClose, onAccept, onReject, updating }) => {
  const getNowDefaults = () => {
    const now = new Date();
    return {
      date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`,
    };
  };

  const [estDate,   setEstDate]   = useState(getNowDefaults().date);
  const [estHour,   setEstHour]   = useState("12");
  const [estMinute, setEstMinute] = useState("00");
  const [estPeriod, setEstPeriod] = useState("PM");

  if (!order) return null;

  const todayStr = new Date().toISOString().split("T")[0];

  const buildEstimatedTime = () => {
    if (!estDate) return undefined;
    let hour24 = parseInt(estHour, 10);
    if (estPeriod === "AM" && hour24 === 12) hour24 = 0;
    if (estPeriod === "PM" && hour24 !== 12) hour24 += 12;
    return new Date(`${estDate}T${String(hour24).padStart(2, "0")}:${estMinute}:00`).toISOString();
  };

  const selectStyle = {
    flex: 1, padding: "9px 10px", borderRadius: 8,
    border: "1.5px solid var(--mly-border)", fontSize: 13,
    outline: "none", color: "var(--mly-text)", background: "var(--mly-surface)", cursor: "pointer",
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)", zIndex: 1040,
      }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1050, width: "100%", maxWidth: 520,
        background: "var(--mly-surface)", borderRadius: 20,
        boxShadow: "0 24px 64px rgba(0,0,0,0.16)",
        padding: "32px 32px 28px",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--mly-text)" }}>Confirm Action</h2>
              <p style={{ marginTop: 10, fontSize: 15, color: "var(--mly-text-soft)", lineHeight: 1.6, maxWidth: 380 }}>
                Set an estimated delivery time, then{" "}
                <span style={{ fontWeight: 700, color: "#185fa5" }}>accept</span> or{" "}
                <span style={{ fontWeight: 700, color: "#dc2626" }}>cancel</span> this order.
              </p>
            </div>
            <button onClick={onClose} style={{
              width: 32, height: 32, borderRadius: "50%",
              border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)",
              cursor: "pointer", fontSize: 18, color: "var(--mly-text-soft)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>×</button>
          </div>

          {/* Date */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--mly-text-soft)" }}>
              Approximate Delivery / Pickup Date
            </label>
            <input
              type="date"
              value={estDate}
              min={todayStr}
              onChange={(e) => setEstDate(e.target.value)}
              style={{
                padding: "9px 12px", borderRadius: 8,
                border: "1.5px solid var(--mly-border)", fontSize: 13,
                outline: "none", color: "var(--mly-text)", fontFamily: "inherit", width: "100%",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#185fa5")}
              onBlur={(e)  => (e.target.style.borderColor = "var(--mly-border)")}
            />
          </div>

          {/* Time */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--mly-text-soft)" }}>
              Approximate Time
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <select value={estHour} onChange={(e) => setEstHour(e.target.value)} style={selectStyle}
                onFocus={(e) => (e.target.style.borderColor = "#185fa5")}
                onBlur={(e)  => (e.target.style.borderColor = "var(--mly-border)")}>
                {Array.from({ length: 12 }, (_, i) => {
                  const h = String(i + 1).padStart(2, "0");
                  return <option key={h} value={h}>{h}</option>;
                })}
              </select>
              <select value={estMinute} onChange={(e) => setEstMinute(e.target.value)} style={selectStyle}
                onFocus={(e) => (e.target.style.borderColor = "#185fa5")}
                onBlur={(e)  => (e.target.style.borderColor = "var(--mly-border)")}>
                {["00", "15", "30", "45"].map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <select value={estPeriod} onChange={(e) => setEstPeriod(e.target.value)} style={selectStyle}
                onFocus={(e) => (e.target.style.borderColor = "#185fa5")}
                onBlur={(e)  => (e.target.style.borderColor = "var(--mly-border)")}>
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={() => onReject(order.orderNo)} disabled={updating}
              style={{
                flex: 1, padding: "12px 0", borderRadius: 10,
                border: "1.5px solid #fca5a5",
                background: updating ? "#fef2f2" : "#fff1f1",
                color: "#dc2626", fontWeight: 700, fontSize: 14,
                cursor: updating ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
              onMouseEnter={(e) => { if (!updating) e.currentTarget.style.background = "#fee2e2"; }}
              onMouseLeave={(e) => { if (!updating) e.currentTarget.style.background = "#fff1f1"; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5.3 5.3a1.2 1.2 0 0 1 1.7 0L12 10.3l5-5a1.2 1.2 0 1 1 1.7 1.7l-5 5 5 5a1.2 1.2 0 0 1-1.7 1.7l-5-5-5 5a1.2 1.2 0 0 1-1.7-1.7l5-5-5-5a1.2 1.2 0 0 1 0-1.7Z" /></svg>
              Cancel Order
            </button>
            <button
              onClick={() => onAccept(order.orderNo, buildEstimatedTime())} disabled={updating}
              style={{
                flex: 1, padding: "12px 0", borderRadius: 10, border: "none",
                background: updating ? "#90b8e0" : "#185fa5",
                color: "#fff", fontWeight: 700, fontSize: 14,
                cursor: updating ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
              onMouseEnter={(e) => { if (!updating) e.currentTarget.style.background = "#1251a3"; }}
              onMouseLeave={(e) => { if (!updating) e.currentTarget.style.background = "#185fa5"; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.7 5.3a1.3 1.3 0 0 1 0 1.9L10.2 17.7a1.3 1.3 0 0 1-1.9 0l-5-5a1.3 1.3 0 0 1 1.9-1.9l4.1 4.1 9.5-9.6a1.3 1.3 0 0 1 1.9 0Z" /></svg>
              Accept Order
            </button>
          </div>

        </div>
      </div>
    </>
  );
};

const InlineStatusModal = ({ order, deliveryMode, onClose, onAdvance, onOpenDetails, updating }) => {

  const getNowDefaults = () => {
    const now  = new Date();
    const yyyy = now.getFullYear();
    const mm   = String(now.getMonth() + 1).padStart(2, "0");
    const dd   = String(now.getDate()).padStart(2, "0");
    return { date: `${yyyy}-${mm}-${dd}` };
  };

  const defaults = getNowDefaults();
  const [estDate,   setEstDate]   = useState(defaults.date);
  const [estHour,   setEstHour]   = useState("12");
  const [estMinute, setEstMinute] = useState("00");
  const [estPeriod, setEstPeriod] = useState("PM");

  if (!order) return null;

  const nextStatus = NEXT_STATUS[deliveryMode]?.[order.status];
  const nextLabel  = nextStatus ? STATUS_LABEL[nextStatus] : null;
  const nextColors = nextStatus ? statusChipStyle(nextStatus) : null;
  const isFinal    = !nextStatus;

  const showEstimate = order.status === "pending";
  const todayStr     = new Date().toISOString().split("T")[0];

  const handleAdvance = () => {
    let estimatedDeliveryTime = undefined;
    if (showEstimate && estDate) {
      let hour24 = parseInt(estHour, 10);
      if (estPeriod === "AM" && hour24 === 12) hour24 = 0;
      if (estPeriod === "PM" && hour24 !== 12) hour24 += 12;
      const hh = String(hour24).padStart(2, "0");
      estimatedDeliveryTime = new Date(`${estDate}T${hh}:${estMinute}:00`).toISOString();
    }
    onAdvance(order.orderNo, nextStatus, estimatedDeliveryTime);
  };

  const selectStyle = {
    flex: 1, padding: "9px 10px", borderRadius: 8,
    border: "1.5px solid var(--mly-border)", fontSize: 13,
    outline: "none", color: "var(--mly-text)", background: "var(--mly-surface)",
    cursor: "pointer",
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)", zIndex: 1040,
      }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1050, width: "100%", maxWidth: 480,
        background: "var(--mly-surface)", borderRadius: 20,
        boxShadow: "0 24px 64px rgba(0,0,0,0.16)",
        padding: "28px 28px 24px",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

          {/* ── Header ── */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--mly-text)" }}>Update Order</h2>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--mly-text-muted)" }}>
                {order.orderNo} &nbsp;·&nbsp; {STATUS_LABEL[order.status] || order.status}
              </p>
            </div>
            <button onClick={onClose} style={{
              width: 32, height: 32, borderRadius: "50%",
              border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)",
              cursor: "pointer", fontSize: 18, color: "var(--mly-text-soft)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>×</button>
          </div>

          {/* ── Estimate fields — only on pending status ── */}
          {showEstimate && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

              {/* Date */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "var(--mly-text-soft)" }}>
                  Approximate Delivery / Pickup Date
                </label>
                <input
                  type="date"
                  value={estDate}
                  min={todayStr}
                  onChange={(e) => setEstDate(e.target.value)}
                  style={{
                    padding: "9px 12px", borderRadius: 8,
                    border: "1.5px solid var(--mly-border)", fontSize: 13,
                    outline: "none", color: "var(--mly-text)", fontFamily: "inherit",
                    width: "100%",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#185fa5")}
                  onBlur={(e)  => (e.target.style.borderColor = "var(--mly-border)")}
                />
              </div>

              {/* Time — 12hr format */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "var(--mly-text-soft)" }}>
                  Approximate Time
                </label>
                <div style={{ display: "flex", gap: 8 }}>

                  {/* Hour */}
                  <select
                    value={estHour}
                    onChange={(e) => setEstHour(e.target.value)}
                    style={selectStyle}
                    onFocus={(e) => (e.target.style.borderColor = "#185fa5")}
                    onBlur={(e)  => (e.target.style.borderColor = "var(--mly-border)")}
                  >
                    {Array.from({ length: 12 }, (_, i) => {
                      const h = String(i + 1).padStart(2, "0");
                      return <option key={h} value={h}>{h}</option>;
                    })}
                  </select>

                  {/* Minute */}
                  <select
                    value={estMinute}
                    onChange={(e) => setEstMinute(e.target.value)}
                    style={selectStyle}
                    onFocus={(e) => (e.target.style.borderColor = "#185fa5")}
                    onBlur={(e)  => (e.target.style.borderColor = "var(--mly-border)")}
                  >
                    {["00", "15", "30", "45"].map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>

                  {/* AM / PM */}
                  <select
                    value={estPeriod}
                    onChange={(e) => setEstPeriod(e.target.value)}
                    style={selectStyle}
                    onFocus={(e) => (e.target.style.borderColor = "#185fa5")}
                    onBlur={(e)  => (e.target.style.borderColor = "var(--mly-border)")}
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>

                </div>
              </div>

            </div>
          )}

          {/* ── Buttons ── */}
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={onClose} disabled={updating}
              style={{
                flex: isFinal ? 1 : "0 0 auto",
                padding: "11px 20px", borderRadius: 10,
                border: "1.5px solid var(--mly-border)",
                background: "var(--mly-surface-2)",
                color: "var(--mly-text-soft)", fontWeight: 700, fontSize: 13,
                cursor: updating ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                whiteSpace: "nowrap",
              }}
            >
              Close
            </button>
            {nextLabel && (
              <button
                onClick={handleAdvance}
                disabled={updating}
                style={{
                  flex: 1, padding: "11px 16px", borderRadius: 10, border: "none",
                  background: updating ? "#9CC7B4" : BRAND.pine,
                  color: "#fff", fontWeight: 700, fontSize: 13,
                  cursor: updating ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.7 5.3a1.3 1.3 0 0 1 0 1.9L10.2 17.7a1.3 1.3 0 0 1-1.9 0l-5-5a1.3 1.3 0 0 1 1.9-1.9l4.1 4.1 9.5-9.6a1.3 1.3 0 0 1 1.9 0Z" /></svg>
                Mark as {nextLabel}
              </button>
            )}
          </div>

          <div style={{ fontSize: 11, color: "var(--mly-text-muted)", textAlign: "center" }}>
            Need assignment, failed delivery or cancel?{" "}
            <span
              onClick={() => onOpenDetails(order.orderNo)}
              style={{ color: "var(--mly-accent)", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
            >
              Open order details
            </span>
          </div>

        </div>
      </div>
    </>
  );
};

/* ─── Main Component ─────────────────────────────────────────── */
// ✅ Accept deliveryMode & onModeChange from AdminApp via props
const ListOrder = ({ deliveryMode: propDeliveryMode, onModeChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tableRef = useRef(null);

  // ── Use prop as initial value; fall back to "delivery" ──────
  const [deliveryMode,       setDeliveryMode]       = useState(propDeliveryMode || "delivery");
  const [activeFilter,       setActiveFilter]       = useState("new");
  const [dateFilter,         setDateFilter]         = useState("all");
  const [pendingActionOrder, setPendingActionOrder] = useState(null);
  const [inlineActionOrder,  setInlineActionOrder]  = useState(null);
  const [updating,           setUpdating]           = useState(false);
  const [page,               setPage]               = useState(1);
  const [searchTerm,         setSearchTerm]         = useState("");

  const orders          = useSelector(selectAllOrders);
  const allStatusCounts = useSelector(selectStatusCounts);
  const statusCounts    = allStatusCounts[deliveryMode] || {};
  const loading         = useSelector((s) => s.orders.loading);
  const pagination      = useSelector((s) => s.orders.pagination);
  const error           = useSelector((s) => s.orders.error);

  const limit = 10;

  const currentFilter = STATUS_FILTERS.find((f) => f.key === activeFilter) || STATUS_FILTERS[0];
  const statusParam   = currentFilter.statuses.join(",");

  // ✅ KEY: Sync local state when header pill is clicked (prop changes)
  useEffect(() => {
    if (propDeliveryMode && propDeliveryMode !== deliveryMode) {
      setDeliveryMode(propDeliveryMode);
      setActiveFilter("new");
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propDeliveryMode]);

  /* The typed search, held back until the typing stops. Every keystroke is
     otherwise a query across every order in the shop. */
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => { setSearchQuery(searchTerm.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  /* ── Fetch orders when mode / filter / page / search changes ── */
  useEffect(() => {
    dispatch(fetchOrders({ page, limit, status: statusParam, deliveryMode, q: searchQuery }));
  }, [dispatch, deliveryMode, statusParam, page, searchQuery]);

  /* ── Fetch counts for every filter chip on mode change ── */
  useEffect(() => {
    dispatch(fetchStatusCounts({ deliveryMode, statuses: COUNTED_STATUSES }));
  }, [dispatch, deliveryMode]);

  /* ── Re-fetch after status update ── */
  const refreshAll = () => {
    dispatch(fetchOrders({ page, limit, status: statusParam, deliveryMode, q: searchQuery }));
    dispatch(fetchStatusCounts({ deliveryMode, statuses: COUNTED_STATUSES }));
  };

  /* A chip's number is the sum of its own statuses — status-counts is keyed
     by single status, so a set key like "pending,accepted" reads as blank. */
  const countFor = (statuses) =>
    statuses.reduce((n, s) => n + (Number(statusCounts[s]) || 0), 0);

  /* ── Live updates: poll GET /admin/orders/activity every 10s ──
     latestOrderAt advances  → brand toast + full refresh (list + chips)
     latestUpdateAt advances → silent list refresh only.
     Refs keep the interval stable while always using current params. */
  const activitySeenRef = useRef(null); // { order, update } last seen
  const liveRefreshRef  = useRef({ all: () => {}, list: () => {} });
  useEffect(() => {
    liveRefreshRef.current = {
      all:  refreshAll,
      list: () => dispatch(fetchOrders({ page, limit, status: statusParam, deliveryMode, q: searchQuery })),
    };
  });

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        /* The body IS the object: {latestOrderAt, latestUpdateAt, newCountToday},
           and a branch with no orders reports null rather than 1970. */
        const res  = await api.get("/admin/orders/activity");
        const data = res.data || {};
        const latestOrder  = data.latestOrderAt  || null;
        const latestUpdate = data.latestUpdateAt || null;
        if (cancelled) return;
        const seen = activitySeenRef.current;
        if (!seen) {
          /* First poll seeds the baseline — no toast for pre-existing orders */
          activitySeenRef.current = { order: latestOrder, update: latestUpdate };
          return;
        }
        const advanced = (next, prev) =>
          !!next && (!prev || new Date(next).getTime() > new Date(prev).getTime());
        const orderAdvanced  = advanced(latestOrder,  seen.order);
        const updateAdvanced = advanced(latestUpdate, seen.update);
        activitySeenRef.current = {
          order:  orderAdvanced  ? latestOrder  : seen.order,
          update: updateAdvanced ? latestUpdate : seen.update,
        };
        if (orderAdvanced) {
          toast("🌾 New order received", {
            style: {
              background: BRAND.forest, color: BRAND.cream,
              fontWeight: 700, borderRadius: 12,
            },
          });
          liveRefreshRef.current.all();   // list + chip counts
        } else if (updateAdvanced) {
          liveRefreshRef.current.list();  // silent list refresh
        }
      } catch {
        /* activity endpoint unavailable (backend not deployed yet) — ignore */
      }
    };
    poll();
    const timer = setInterval(poll, 10000);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // ✅ Switch mode from INSIDE the table tabs — also notify parent
  const handleModeSwitch = (mode) => {
    setDeliveryMode(mode);
    onModeChange?.(mode);   // ← keep header in sync
    setActiveFilter("new");
    setPage(1);
  };

  const handleFilterSwitch = (key) => {
    setActiveFilter(key);
    setPage(1);
  };

  /* ── Contract endpoint: POST /fulfilment/orders/:orderNo/status ──
     Body is {status, note?, reason?} and the reply is {orderNo, from, to} —
     not the order — so every caller refetches. The transition graph is the
     authority: a move it refuses comes back 409 illegal_transition with a
     message that names both states. */
  const patchStatus = async (orderNo, status, extra = {}) => {
    setUpdating(true);
    try {
      await api.post(`/fulfilment/orders/${orderNo}/status`, { status, ...extra });
      return true;
    } catch (err) {
      const code = err.response?.status;
      const msg  = err.response?.data?.message;
      window.alert(
        code === 409 ? (msg || "That status change is not allowed from here.")
        : code === 404 ? "Order not found."
        : code === 403 ? "This order belongs to another store."
        : msg || "Failed to update order status"
      );
      return false;
    } finally {
      setUpdating(false);
    }
  };

  /* TODO(cutover): no server endpoint for the estimate — POST /fulfilment/
     orders/{orderNo}/status takes only {status, note, reason} and nothing in
     the service writes estimated_delivery_at, so the modal's date/time is
     collected and goes nowhere. Left un-sent rather than posted into a field
     the deserializer discards. */
  const handleAccept = async (orderNo, _estimatedDeliveryTime) => {
    await patchStatus(orderNo, "accepted");
    setPendingActionOrder(null);
    refreshAll();
  };

  const handleReject = async (orderNo) => {
    if (!window.confirm("Cancel this order?")) return;
    await patchStatus(orderNo, "cancelled");
    setPendingActionOrder(null);
    refreshAll();
  };

  /* TODO(cutover): no server endpoint for the estimate — see handleAccept. */
  const handleInlineAdvance = async (orderNo, nextStatus, _estimatedDeliveryTime) => {
    await patchStatus(orderNo, nextStatus);
    setInlineActionOrder(null);
    refreshAll();
  };

  // Date preset (client-side, IST) narrows the already-status-filtered
  // rows; search then narrows further. Status logic itself is untouched.
  const dateScoped = orders.filter((o) => dateInRange(o.placedAt, dateFilter));

  /* No second pass. The server searches order number, bill number, shipping
     name, email and phone, and now the customer's own name and number as well
     -- which is what a pickup order has instead of a shipping snapshot. A
     filter applied afterwards over the rows it returned can only do one thing:
     remove rows it matched on a field this list does not carry. It did exactly
     that, dropping every row matched by bill number or shipping phone. */
  const filtered = dateScoped;

  return (
    <div>
      <style>{`
        @keyframes blinkWarning {
          0%   { opacity: 1; transform: scale(1); }
          50%  { opacity: 0.4; transform: scale(1.15); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <div className="container-fluid" style={{ marginBottom: "20px", top: "10px", position: "relative" }}>
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>

          {/* ── Delivery Mode Tabs ── */}
          <div style={{ display: "flex", borderBottom: "1px solid var(--mly-border-soft)" }}>
            {DELIVERY_MODES.map((mode) => {
              const isActive = deliveryMode === mode.value;
              return (
                <button
                  key={mode.value}
                  onClick={() => handleModeSwitch(mode.value)}
                  style={{
                    flex: 1, padding: "16px 20px",
                    fontSize: 14, fontWeight: isActive ? 600 : 500,
                    cursor: "pointer", border: "none",
                    borderBottom: isActive ? "3px solid #0f6e56" : "3px solid transparent",
                    background: isActive ? "#e1f5ee" : "var(--mly-surface)",
                    color: isActive ? "#0f6e56" : "var(--mly-text-muted)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    transition: "all 0.15s",
                  }}
                >
                  {mode.value === "delivery" ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M1.6 5.6a1.4 1.4 0 0 1 1.4-1.4h10.2a1.4 1.4 0 0 1 1.4 1.4v1.9h2.8a1.8 1.8 0 0 1 1.5.8l2.9 4.3a1.8 1.8 0 0 1 .3 1v2.6a1.4 1.4 0 0 1-1.4 1.4h-.9a3.1 3.1 0 0 0-6.1 0H9.7a3.1 3.1 0 0 0-6.1 0H3a1.4 1.4 0 0 1-1.4-1.4Zm13 4.1v3h5.2l-2-3ZM6.6 16.9a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm10.9 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" /></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.7 2.4a1.1 1.1 0 0 0-1.4 0L2.9 9.1A1.8 1.8 0 0 0 2.2 10.5v8.7A2.4 2.4 0 0 0 4.6 21.6h3.9v-6.3a1.2 1.2 0 0 1 1.2-1.2h4.6a1.2 1.2 0 0 1 1.2 1.2v6.3h3.9a2.4 2.4 0 0 0 2.4-2.4v-8.7a1.8 1.8 0 0 0-.7-1.4Z" /></svg>
                  )}
                  {mode.label}
                </button>
              );
            })}
          </div>

          {/* ── Status Filter Chips ── */}
          <div style={{
            display: "flex", flexWrap: "wrap", alignItems: "center",
            padding: "12px 20px", background: "var(--mly-surface-2)",
            borderBottom: "1px solid var(--mly-border-soft)", gap: 8,
          }}>
            {STATUS_FILTERS.map((f) => {
              const isActive = activeFilter === f.key;
              /* Every chip counts the same way. The active one used to show
                 pagination.total instead, which is the count under the CURRENT
                 search -- so typing a phone number left one chip saying 1
                 beside chips saying 40, as though the other stages had emptied
                 out. status-counts is scoped exactly like the list, so this is
                 the same number, unnarrowed, for all of them. */
              const count    = f.statuses.length ? countFor(f.statuses) : null;
              // A stage with no orders is not a gate anyone needs — hide
              // its chip ("All" always stays; the active chip stays so
              // the selection never vanishes under the user).
              if (f.statuses.length && !isActive && (count ?? 0) === 0) {
                return null;
              }
              const chip = f.statuses.length
                ? statusChipStyle(f.statuses[0])
                : { bg: "#f3f4f6", color: BRAND.ink, border: "#d1d5db" };
              const showWarning = f.key === "new" && (count || 0) > 0;

              return (
                <button
                  key={f.key}
                  onClick={() => handleFilterSwitch(f.key)}
                  style={{
                    padding: "6px 14px",
                    fontSize: 12, fontWeight: isActive ? 700 : 600,
                    cursor: "pointer",
                    borderRadius: 20,
                    border: `1.5px solid ${isActive ? chip.color : chip.border}`,
                    background: isActive ? chip.color : chip.bg,
                    color: isActive ? "#fff" : chip.color,
                    whiteSpace: "nowrap",
                    display: "flex", alignItems: "center", gap: 6,
                    transition: "all 0.15s",
                  }}
                >
                  {showWarning && (
                    <span style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      animation: "blinkWarning 1s infinite",
                    }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                        stroke={isActive ? "#fff" : "#f59e0b"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                    </span>
                  )}
                  {f.label}
                  {count !== null && count > 0 && (
                    <span style={{
                      background: isActive ? "rgba(255,255,255,0.25)" : "#fff",
                      color:      isActive ? "#fff" : chip.color,
                      border:     `1px solid ${isActive ? "rgba(255,255,255,0.4)" : chip.border}`,
                      fontSize: 10, fontWeight: 700,
                      padding: "1px 7px", borderRadius: 20,
                    }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Date-range Filter Chips (client-side, IST) ── */}
          <div style={{
            display: "flex", flexWrap: "wrap", alignItems: "center",
            padding: "10px 20px", background: "var(--mly-surface)",
            borderBottom: "1px solid var(--mly-border-soft)", gap: 8,
          }}>
            {DATE_PRESETS.map((d) => {
              const isActive = dateFilter === d.key;
              return (
                <button
                  key={d.key}
                  onClick={() => { setDateFilter(d.key); setPage(1); }}
                  style={{
                    padding: "6px 14px",
                    fontSize: 12, fontWeight: isActive ? 700 : 600,
                    cursor: "pointer",
                    borderRadius: 20,
                    border: `1.5px solid ${isActive ? BRAND.forest : "var(--mly-border)"}`,
                    background: isActive ? BRAND.forest : "var(--mly-surface-3)",
                    color: isActive ? "#fff" : "var(--mly-text)",
                    whiteSpace: "nowrap",
                    display: "flex", alignItems: "center", gap: 6,
                    transition: "all 0.15s",
                  }}
                >
                  {d.label}
                </button>
              );
            })}
          </div>

          {/* ── Toolbar ── */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 20px", gap: 12, borderBottom: "1px solid var(--mly-border-soft)",
          }}>
            <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="search-icon" aria-hidden="true"><path d="M10.6 2.4a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4Zm0 2.2a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm5.9 11.3 4.8 4.8a1.2 1.2 0 0 1-1.7 1.7l-4.8-4.8a9.6 9.6 0 0 0 1.7-1.7Z" /></svg>
              <input
                type="text"
                placeholder="Search by order no, name, email or phone…"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                style={{
                  paddingLeft: 34, paddingRight: 12, paddingTop: 9, paddingBottom: 9,
                  borderRadius: 10, border: "1.5px solid var(--mly-border)",
                  fontSize: 13, width: "100%", outline: "none",
                  background: "var(--mly-surface)", color: "var(--mly-text)", fontFamily: "inherit",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#185fa5")}
                onBlur={(e)  => (e.target.style.borderColor = "var(--mly-border)")}
              />
            </div>
            <span style={{ fontSize: 12, color: "var(--mly-text-muted)", whiteSpace: "nowrap" }}>
              {filtered.length} order{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* ── Table ── */}
          <div style={{ overflowX: "auto" }}>
            {/* Only block on the very first load — background/live refreshes
                keep the current rows on screen (silent update). */}
            {loading && orders.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 0", color: "var(--mly-text-muted)", fontSize: 14 }}>
                Loading orders…
              </div>
            ) : error ? (
              <div style={{ textAlign: "center", padding: "48px 0", color: "#ef4444", fontSize: 14 }}>
                {error}
              </div>
            ) : (
              <table
                className="display table table-bordered"
                ref={tableRef}
                style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
              >
                <thead>
                  <tr style={{ background: "var(--mly-surface-2)" }}>
                    {[
                      { label: "Si No",    width: 60  },
                      { label: "Order ID", width: 120 },
                      { label: "Customer", width: 170 },
                      { label: "Total",    width: 100 },
                      { label: "Date",     width: 100 },
                      { label: "Payment",  width: 150 },
                      { label: "Status",   width: 150 },
                      { label: "Miller",   width: 110 },
                      { label: "Delivery", width: 110 },
                      { label: "Action",   width: 110 },
                    ].map(({ label, width }) => (
                      <th key={label} style={{
                        padding: "11px 16px",
                        fontSize: 11, fontWeight: 700, color: "var(--mly-text-muted)",
                        letterSpacing: 0.5, textTransform: "uppercase",
                        borderBottom: "1px solid var(--mly-border-soft)", minWidth: width,
                      }}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={10} style={{ textAlign: "center", padding: "56px 0" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="search-icon" aria-hidden="true"><path d="M10.6 2.4a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4Zm0 2.2a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm5.9 11.3 4.8 4.8a1.2 1.2 0 0 1-1.7 1.7l-4.8-4.8a9.6 9.6 0 0 0 1.7-1.7Z" /></svg>
                          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--mly-text-muted)" }}>No orders found</span>
                          <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
                            {searchTerm ? `No results for "${searchTerm}"` : "No orders in this status yet"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((o, idx) => {
                      const sc = statusChipStyle(o.status);
                      /* Rows carry no id at all — orderNo addresses an order
                         everywhere now, including the detail route. */
                      return (
                        <tr
                          key={o.orderNo}
                          style={{ borderBottom: "1px solid var(--mly-border-soft)", transition: "background 0.1s" }}
                          onMouseEnter={(e) => {
                            e.currentTarget.querySelectorAll("td").forEach((td) => (td.style.background = "var(--mly-surface-2)"));
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.querySelectorAll("td").forEach((td) => (td.style.background = ""));
                          }}
                        >
                          <td style={{ padding: "13px 16px", color: "var(--mly-text-muted)", fontSize: 12 }}>
                            {(page - 1) * limit + idx + 1}
                          </td>
                          <td style={{ padding: "13px 16px" }}>
                            <span style={{ fontWeight: 700, color: "var(--mly-heading)", fontSize: 13 }}>
                              {o.orderNo || "—"}
                            </span>
                            {(o.splitCount || 1) > 1 && (
                              <span
                                title={o.billNo ? `Bill ${o.billNo}` : "Part of a split bill"}
                                style={{
                                  marginLeft: 6,
                                  background: BRAND.cream, color: BRAND.gold,
                                  border: `1px solid ${BRAND.honey}`,
                                  fontSize: 10, fontWeight: 700,
                                  padding: "2px 8px", borderRadius: 20,
                                  whiteSpace: "nowrap", display: "inline-block",
                                }}
                              >
                                Split {o.splitIndex || 1}/{o.splitCount}
                              </span>
                            )}
                          </td>
                       <td style={{ padding: "13px 16px" }}>
  {/* A pickup has no shipping snapshot; the customer record is on every row now. */}
  <div style={{ fontWeight: 600, fontSize: 13 }}>
    {o.fulfilment === "pickup"
      ? (o.customerName || "—")
      : (o.shipName || "—")}
  </div>
  <div style={{ color: "var(--mly-text-muted)", fontSize: 11, marginTop: 2 }}>
    {o.fulfilment === "pickup"
      ? (o.customerPhone || "—")
      : (o.shipPhone || "—")}
  </div>
</td>
                          <td style={{ padding: "13px 16px", fontWeight: 700, fontSize: 13 }}>
                            ₹{rupees(o.totalPaise)}
                          </td>
                          <td style={{ padding: "13px 16px", fontSize: 12, color: "var(--mly-text-muted)" }}>
                            {formatDate(o.placedAt)}
                          </td>
                          <td style={{ padding: "13px 16px" }}>
                            <span style={{
                              background: o.paymentStatus === "paid" ? "#e1f5ee" : "#faeeda",
                              color:      o.paymentStatus === "paid" ? "#0f6e56" : "#854f0b",
                              border:     `1px solid ${o.paymentStatus === "paid" ? "#9fe1cb" : "#fac775"}`,
                              fontSize: 11, fontWeight: 700,
                              padding: "3px 10px", borderRadius: 20,
                              display: "inline-flex", alignItems: "center", gap: 4,
                            }}>
                            {
  o.paymentMethod?.toUpperCase() === "RAZORPAY"
    ? "Online Payment"
    : (o.paymentMethod?.toUpperCase() || "—")
}
&nbsp;·&nbsp;
{
  (o.paymentStatus || "—").charAt(0).toUpperCase() +
  (o.paymentStatus || "").slice(1)
}
                            </span>
                          </td>
                          <td style={{ padding: "13px 16px" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <span style={{
                                background: sc.bg, color: sc.color,
                                border: `1px solid ${sc.border}`,
                                fontSize: 11, fontWeight: 700,
                                padding: "4px 10px", borderRadius: 20, whiteSpace: "nowrap",
                              }}>
                                {STATUS_LABEL[o.status] || o.status}
                              </span>
                              {o.leaveAtDoor && (
                                <span title="Leave at door requested" style={{ fontSize: 15, cursor: "default" }}>
                                  🚪
                                </span>
                              )}
                            </span>
                          </td>
                          <td style={{ padding: "13px 16px", fontSize: 12, color: o.millerName ? "var(--mly-text)" : "var(--mly-text-muted)", fontWeight: o.millerName ? 600 : 400 }}>
                            {o.millerName || "—"}
                          </td>
                          <td style={{ padding: "13px 16px", fontSize: 12, color: o.deliveryName ? "var(--mly-text)" : "var(--mly-text-muted)", fontWeight: o.deliveryName ? 600 : 400 }}>
                            {o.deliveryName || "—"}
                          </td>
                          <td style={{ padding: "13px 16px" }}>
                            {/* One entry point: the details page carries the
                                timeline, assignment and every allowed action. */}
                            <button
                              onClick={() => navigate(`/admin/orders/details/${o.orderNo}`)}
                              title="Open order — view details & take actions"
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 7,
                                padding: "8px 14px", borderRadius: 10, border: "none",
                                background: "var(--milliyo-forest, #0E3A2C)", color: "#FDF6E3",
                                fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = "#1A5440")}
                              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--milliyo-forest, #0E3A2C)")}
                            >
                              Manage
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="#C9952A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14"/>
                                <path d="M12 5l7 7-7 7"/>
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Pagination ── */}
          {pagination?.totalPages > 1 && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 20px", borderTop: "1px solid var(--mly-border-soft)",
            }}>
              <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
                Page {page} of {pagination.totalPages} &nbsp;·&nbsp; {pagination.total} orders
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  style={{
                    padding: "6px 14px", borderRadius: 8,
                    border: "1px solid var(--mly-border)", background: "var(--mly-surface)",
                    color: page === 1 ? "var(--mly-text-muted)" : "var(--mly-text-soft)",
                    fontSize: 12, cursor: page === 1 ? "not-allowed" : "pointer",
                  }}
                >← Prev</button>

                {/* A window around the page in view, not one button per page.
                    At five thousand orders this rendered five hundred buttons
                    on every render -- and this list re-renders every ten
                    seconds, so the whole table stuttered on each refresh.
                    Nobody has ever clicked "page 313" out of a row of them. */}
                {pageWindow(page, pagination.totalPages).map((n) => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    style={{
                      padding: "6px 12px", borderRadius: 8,
                      border: page === n ? "none" : "1px solid var(--mly-border)",
                      background: page === n ? "#185fa5" : "var(--mly-surface)",
                      color: page === n ? "#fff" : "var(--mly-text-soft)",
                      fontSize: 12, fontWeight: page === n ? 700 : 400,
                      cursor: "pointer",
                    }}
                  >{n}</button>
                ))}

                <button
                  disabled={page === pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  style={{
                    padding: "6px 14px", borderRadius: 8,
                    border: "1px solid var(--mly-border)", background: "var(--mly-surface)",
                    color: page === pagination.totalPages ? "var(--mly-text-muted)" : "var(--mly-text-soft)",
                    fontSize: 12,
                    cursor: page === pagination.totalPages ? "not-allowed" : "pointer",
                  }}
                >Next →</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {pendingActionOrder && (
        <PendingActionModal
          order={pendingActionOrder}
          onClose={() => setPendingActionOrder(null)}
          onAccept={handleAccept}
          onReject={handleReject}
          updating={updating}
        />
      )}

      {inlineActionOrder && (
        <InlineStatusModal
          order={inlineActionOrder}
          deliveryMode={deliveryMode}
          onClose={() => setInlineActionOrder(null)}
          onAdvance={handleInlineAdvance}
          onOpenDetails={(id) => navigate(`/admin/orders/details/${id}`)}
          updating={updating}
        />
      )}
    </div>
  );
};

export default ListOrder;
